import { Box, TextField, Typography } from '@mui/material';
import { getAuth, isSignInWithEmailLink } from 'firebase/auth';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import UserMethods from '../../actions/user_action';
import { TextInput } from '../../helpers/forms';
import { isValidEmail } from '../../helpers/strings';
import { Namespace } from '../../locales/translations';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import ActionButton from '../_include/Buttons/ActionButton';
import { AppRoutes } from '../../constants/routes';
import EmailRounded from '@mui/icons-material/EmailRounded';
import KeyRounded from '@mui/icons-material/KeyRounded';
import { stringifyUrl } from 'query-string';

type LoginPageProps = {}

type InputTypes = {
    email: TextInput;
}

function LoginPage(props: LoginPageProps) {
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const emailParam = searchParams.get('email');
    const { t } = useTranslation([Namespace.ACTIONS, Namespace.FORMS, Namespace.TITLES,]);
    const userLoading = useAppSelector(state => state.user.loading);
    const userAuth = useAppSelector(state => state.user.isAuthenticated);

    const [inputs, setInputs] = useState<InputTypes>({
        email: {
            value: emailParam ?? "",
            error: null,
        },
    });

    const { value: email, error: emailError } = inputs.email;
    const signInAttemptedRef = useRef(false); // Track if sign-in was attempted

    const handleEmailChanged = (value: string) => {
        setInputs({
            ...inputs,
            email: {
                value,
                error: null,
            }
        });
    }

    const handleEmailLinkPressed = () => {
        if (isValidEmail(email)) {
            dispatch(UserMethods.requestMagicLink(email));
        }
        else { // invalid email
            setInputs({
                ...inputs,
                email: {
                    ...inputs.email,
                    error: t("sign_in.invalid_email", { ns: Namespace.FORMS }),
                },
            });
        }
    }
    
    /** Login if coming from a Firebase auth magic link */
    useEffect(() => {
        const link = window.location.href;

        // log user in with Firebase auth
        const auth = getAuth();
        if (isSignInWithEmailLink(auth, link) && !userAuth && !signInAttemptedRef.current) {
            signInAttemptedRef.current = true; // Set to true to prevent multiple sign-in attempts
            
            // check if email is set in URL params
            if (!emailParam) {
                alert("Invalid link");
                return;
            }

            dispatch(UserMethods.signInWithMagicLink(emailParam, link));
        }

    }, [dispatch, emailParam, userAuth]);


    const invitationToken = searchParams.get('invitation');

    /** Login with invitation token */
    useEffect(() => {
        if (invitationToken) {
            dispatch(UserMethods.signInWithToken(invitationToken));
        }
    }, [dispatch, invitationToken]);

    return (
        <Box
            display="flex"
            flexDirection="column"
            flexGrow={1}
            justifyContent="center"
            alignItems="center"
            px={2}
            maxWidth="500px"
            mx="auto"
        >
            <Typography variant="h2" align="center" sx={{ mb: 5, fontWeight: 'bold' }}>
            {t("dashboard", { ns: Namespace.TITLES })}
            </Typography>
            
            <Box width="100%" sx={{ mb: 2 }}>
                <TextField
                    placeholder={t("sign_in.email", { ns: Namespace.FORMS }) ?? ""}
                    value={email}
                    variant="outlined"
                    type="email"
                    onChange={(event) => handleEmailChanged(event.target.value)}
                    disabled={userLoading}
                    error={emailError !== null}
                    helperText={emailError}
                    fullWidth
                    sx={{
                        mb: 2,
                    }}
                />
                
                <ActionButton
                    color="primary"
                    fullWidth={true}
                    startIcon={<EmailRounded />}
                    disabled={!email}
                    loading={userLoading}
                    onClick={handleEmailLinkPressed}
                    type="submit"
                    style={{
                        marginBottom: 20,
                        minWidth: 220,
                    }}
                >
                    {t("sign_in_with_link", { ns: Namespace.ACTIONS })}
                </ActionButton>
                
                <Link 
                    to={stringifyUrl({
                        url: AppRoutes.LOGIN_PASSWORD,
                        query: { email }
                    })}
                    style={{ textDecoration: 'none', width: '100%', display: 'block' }}
                >
                    <ActionButton
                        color="secondary"
                        fullWidth={true}
                        startIcon={<KeyRounded />}
                        loading={userLoading}
                        type="submit"
                        style={{
                            minWidth: 220,
                        }}
                    >
                        {t("sign_in_with_password", { ns: Namespace.ACTIONS })}
                    </ActionButton>
                </Link>
            </Box>
        </Box>
    )
}

export default LoginPage;